import { Link } from "react-router-dom";
import { Zap, ArrowLeft } from "lucide-react";

const SECTIONS = [
  {
    title: "The Service",
    items: [
      "Fit50 provides guided workout sessions of 10, 20, 30, 40 and 50 minutes, an exercise library, meal recipes and related features",
      "The app is currently in founding-member early access; features may evolve as we approach full launch",
      "You must be 18 or older to create an account",
    ],
  },
  {
    title: "Subscriptions & Billing",
    items: [
      "Paid plans (Monthly, Annual, Lifetime) are billed via Stripe on the web and RevenueCat in the app stores",
      "Founding-member pricing is locked for life for as long as your subscription stays active",
      "Subscriptions renew automatically until cancelled; Lifetime is a one-time payment",
      "You can cancel anytime — access continues until the end of the paid period",
    ],
  },
  {
    title: "Health & Safety",
    items: [
      "Fit50 is not medical advice — consult your physician before beginning any exercise program",
      "You train at your own risk; stop immediately if you feel pain, dizziness or discomfort",
      "Heart-rate coaching is guidance, not a medical monitoring device",
    ],
  },
  {
    title: "Acceptable Use",
    items: [
      "One account per person; don't share your login or resell access",
      "Don't copy, scrape or redistribute fit50 content, videos or recipes",
      "We may suspend accounts that abuse the service or other members",
    ],
  },
  {
    title: "Liability & Changes",
    items: [
      "To the extent permitted by law, fit50 is not liable for injuries or indirect losses arising from use of the service",
      "All content and branding remain the property of Fit50 Performance Inc.",
      "We may update these terms; material changes will be announced by email before they take effect",
    ],
  },
];

export const Terms = () => (
  <div data-testid="terms-page" className="min-h-screen bg-obsidian relative">
    <div className="absolute inset-0 grid-lines" />
    <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-14">
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <span className="grid place-items-center h-8 w-8 bg-volt text-white transition-transform duration-300 group-hover:rotate-12">
            <Zap className="h-4 w-4" strokeWidth={3} />
          </span>
          <span className="font-display font-black text-2xl tracking-tight uppercase">
            Fit<span className="text-volt">/</span>50
          </span>
        </Link>
        <Link
          data-testid="terms-back-link"
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[11px] tracking-widest uppercase text-neutral-500 hover:text-volt transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </Link>
      </div>

      <div className="mt-14">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-volt">Legal</p>
        <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-3">
          Terms of Service
        </h1>
        <p className="mt-3 font-mono text-[11px] tracking-widest uppercase text-neutral-500">
          Fit50 • fit50app.com • Effective September 2026
        </p>
        <p className="mt-5 text-sm sm:text-base text-neutral-400 leading-relaxed">
          By using fit50 you agree to these terms. Plain-English summary: train safe, pay for
          what you use, and we&rsquo;ll keep your founding rate forever.
        </p>
      </div>

      <div className="mt-10 space-y-4">
        {SECTIONS.map((s, i) => (
          <section key={s.title} data-testid={`terms-section-${i + 1}`} className="border border-white/10 bg-carbon p-7">
            <h2 className="font-display font-extrabold uppercase text-xl sm:text-2xl">
              <span className="text-volt mr-3">{String(i + 1).padStart(2, "0")}</span>
              {s.title}
            </h2>
            <ul className="mt-4 space-y-2.5">
              {s.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-neutral-400 leading-relaxed">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-volt" />
                  {item}
                </li>
              ))}
            </ul>
          </section>
        ))}

        <section data-testid="terms-section-contact" className="border border-white/10 bg-carbon p-7">
          <h2 className="font-display font-extrabold uppercase text-xl sm:text-2xl">
            <span className="text-volt mr-3">06</span>
            Governing Law &amp; Contact
          </h2>
          <p className="mt-4 text-sm sm:text-base text-neutral-400 leading-relaxed">
            These terms are governed by the laws of New Zealand. Questions? Email{" "}
            <a href="mailto:info@fit50app.com" className="text-volt hover:underline">info@fit50app.com</a>.
          </p>
        </section>
      </div>

      <p className="mt-10 font-mono text-[10px] tracking-widest uppercase text-neutral-600">
        © 2026 Fit50 Performance Inc. All rights reserved.{" "}
        <Link to="/privacy" className="hover:text-volt transition-colors">Privacy Policy</Link>
      </p>
    </div>
  </div>
);
